import { useState, useEffect } from 'react';
import {
  X,
  Milestone,
  Link2,
  DollarSign,
  FlaskConical,
  FileCheck,
  Users,
  Calendar,
  MessageSquare,
  Loader2,
  Link as LinkIcon,
  ChevronDown,
  Sparkles,
  Wand2,
  ArrowRight,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

const POST_TYPES = [
  {
    value: 'pipeline_milestone',
    label: 'Pipeline Milestone',
    labelCn: '管线里程碑',
    icon: Milestone,
    color: 'purple',
    description: 'Phase transitions, IND filings, clinical trial updates',
  },
  {
    value: 'partnership_news',
    label: 'Partnership News',
    labelCn: '合作消息',
    icon: Link2,
    color: 'blue',
    description: 'New partnerships, licensing deals, collaborations',
  },
  {
    value: 'fundraising',
    label: 'Fundraising',
    labelCn: '融资消息',
    icon: DollarSign,
    color: 'green',
    description: 'Series rounds, IPO news, funding announcements',
  },
  {
    value: 'clinical_data',
    label: 'Clinical Data',
    labelCn: '临床数据',
    icon: FlaskConical,
    color: 'orange',
    description: 'Trial results, efficacy data, safety updates',
  },
  {
    value: 'regulatory',
    label: 'Regulatory Update',
    labelCn: '监管进展',
    icon: FileCheck,
    color: 'red',
    description: 'FDA approvals, EMA updates, regulatory milestones',
  },
  {
    value: 'team_update',
    label: 'Team Update',
    labelCn: '团队变动',
    icon: Users,
    color: 'indigo',
    description: 'Key hires, leadership changes, team growth',
  },
  {
    value: 'conference',
    label: 'Conference',
    labelCn: '会议出席',
    icon: Calendar,
    color: 'pink',
    description: 'Conference presentations, poster sessions, speaking events',
  },
  {
    value: 'general',
    label: 'General Update',
    labelCn: '普通更新',
    icon: MessageSquare,
    color: 'gray',
    description: 'Other company news and announcements',
  },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public', description: 'Visible to all visitors' },
  { value: 'buyers_only', label: 'Buyers Only', description: 'Only registered buyers can view' },
  { value: 'draft', label: 'Draft', description: 'Save for later, not published' },
];

// Normalize URL by adding https:// if missing
const normalizeUrl = (url) => {
  if (!url) return '';
  url = url.trim();
  if (!url) return '';
  // If URL doesn't have a protocol, add https://
  if (!url.match(/^https?:\/\//i)) {
    url = 'https://' + url;
  }
  return url;
};

// Blocklist approach: Block known risky platforms instead of whitelisting
const BLOCKED_DOMAINS = [
  // Blog platforms
  'blogspot.com', 'blogger.com', 'wordpress.com', 'medium.com', 'substack.com',
  'tumblr.com', 'wix.com', 'squarespace.com', 'weebly.com', 'ghost.io',
  'hashnode.dev', 'dev.to', 'notion.so', 'carrd.co',
  // URL shorteners (phishing risk)
  'bit.ly', 'tinyurl.com', 'goo.gl', 't.co', 'ow.ly', 'is.gd', 'buff.ly',
  'adf.ly', 'shorte.st', 'bc.vc', 'j.mp', 'v.gd', 'cutt.ly', 'rebrand.ly',
  // Social media (not professional sources)
  'facebook.com', 'fb.com', 'twitter.com', 'x.com', 'instagram.com',
  'tiktok.com', 'snapchat.com', 'pinterest.com', 'reddit.com',
  'weixin.qq.com', 'weibo.com', 'douyin.com', 'xiaohongshu.com',
  // Free hosting / file sharing
  'dropbox.com', 'drive.google.com', 'docs.google.com', 'pastebin.com',
  'mega.nz', 'mediafire.com', 'sendspace.com', 'wetransfer.com',
  // Consumer sites
  'amazon.com', 'ebay.com', 'aliexpress.com', 'taobao.com', 'alibaba.com',
  // Email providers
  'gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'mail.com',
];

// Suspicious TLDs often used for phishing
const SUSPICIOUS_TLDS = ['.tk', '.ml', '.ga', '.cf', '.gq', '.xyz', '.top', '.click', '.link', '.work'];

// Trusted TLDs for professional/official use
const TRUSTED_TLDS = ['.gov', '.edu', '.mil', '.gov.cn', '.edu.cn', '.ac.uk', '.gov.uk', '.org'];

// Validate URL security using blocklist approach
const isAllowedUrl = (url, companyWebsite) => {
  if (!url) return true; // Empty URL is allowed

  try {
    const normalized = normalizeUrl(url);
    const urlObj = new URL(normalized);
    const hostname = urlObj.hostname.toLowerCase();
    const hostWithoutWww = hostname.replace(/^www\./, '');

    // Must be http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }

    // Block IP addresses (common in phishing)
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
      return false;
    }

    // Block suspicious TLDs
    if (SUSPICIOUS_TLDS.some(tld => hostname.endsWith(tld))) {
      return false;
    }

    // Block known risky domains
    if (BLOCKED_DOMAINS.some(domain =>
      hostWithoutWww === domain || hostname.endsWith('.' + domain)
    )) {
      return false;
    }

    // Always allow trusted TLDs (.gov, .edu, .org)
    if (TRUSTED_TLDS.some(tld => hostname.endsWith(tld))) {
      return true;
    }

    // Allow company's own website
    if (companyWebsite) {
      try {
        const companyNormalized = normalizeUrl(companyWebsite);
        const companyHostname = new URL(companyNormalized).hostname.toLowerCase().replace(/^www\./, '');
        if (hostWithoutWww === companyHostname || hostname.endsWith('.' + companyHostname)) {
          return true;
        }
      } catch (e) {
        // Invalid company website URL, skip this check
      }
    }

    // Allow standard corporate TLDs
    const corporateTlds = ['.com', '.co', '.io', '.bio', '.health', '.pharma', '.net',
      '.com.cn', '.cn', '.jp', '.kr', '.de', '.fr', '.uk', '.eu', '.ch', '.au', '.ca'];
    if (corporateTlds.some(tld => hostname.endsWith(tld))) {
      return true;
    }

    return false;
  } catch (e) {
    return false; // Invalid URL
  }
};

export default function PostUpdateModal({ isOpen, onClose, onSuccess, editPost = null }) {
  const { userProfile } = useAuth();
  const [step, setStep] = useState(editPost ? 2 : 1);
  const [selectedType, setSelectedType] = useState(editPost?.post_type || '');
  const [title, setTitle] = useState(editPost?.title || '');
  const [content, setContent] = useState(editPost?.content || '');
  const [externalUrl, setExternalUrl] = useState(editPost?.external_url || '');
  const [visibility, setVisibility] = useState(editPost?.visibility || 'public');
  const [metadata, setMetadata] = useState(editPost?.metadata || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlError, setUrlError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);

  // Get company website for URL validation
  const companyWebsite = userProfile?.sellers?.website;

  useEffect(() => {
    if (editPost) {
      setStep(2);
      setSelectedType(editPost.post_type);
      setTitle(editPost.title);
      setContent(editPost.content);
      setExternalUrl(editPost.external_url || '');
      setVisibility(editPost.visibility);
      setMetadata(editPost.metadata || {});
    }
  }, [editPost]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleExternalUrlChange = (e) => {
    const url = e.target.value;
    setExternalUrl(url);

    // Validate URL on change
    if (url && !isAllowedUrl(url, companyWebsite)) {
      setUrlError('This URL is not allowed. Please use a corporate website, news outlet, or official source. Social media, blogs, and URL shorteners are blocked.');
    } else {
      setUrlError('');
    }
  };

  const handleAiAssist = async (action) => {
    setShowAiMenu(false);
    setAiLoading(true);
    setError('');

    try {
      const response = await api.post('/ai/assist-post', {
        post_type: selectedType,
        title,
        content,
        action,
        company_name: userProfile?.sellers?.company_name,
        therapeutic_areas: userProfile?.sellers?.therapeutic_areas,
      });

      if (response.data?.success && response.data?.data) {
        if (response.data.data.title) {
          setTitle(response.data.data.title);
        }
        if (response.data.data.content) {
          setContent(response.data.data.content);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'AI assistant failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate external URL before submitting
    if (externalUrl && !isAllowedUrl(externalUrl, companyWebsite)) {
      setUrlError('This URL is not allowed. Please use a corporate website, news outlet, or official source.');
      return;
    }

    setLoading(true);

    try {
      // Normalize the external URL (add https:// if missing)
      const normalizedUrl = externalUrl ? normalizeUrl(externalUrl) : null;

      const postData = {
        post_type: selectedType,
        title,
        content,
        external_url: normalizedUrl,
        visibility,
        metadata,
      };

      if (editPost) {
        await api.put(`/posts/${editPost.id}`, postData);
      } else {
        await api.post('/posts', postData);
      }

      onSuccess?.();
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedType('');
    setTitle('');
    setContent('');
    setExternalUrl('');
    setVisibility('public');
    setMetadata({});
    setError('');
    setUrlError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const selectedTypeInfo = POST_TYPES.find(t => t.value === selectedType);

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
      blue: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
      green: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
      orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
      red: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
      pink: 'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100',
      gray: 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100',
    };
    return colors[color] || colors.gray;
  };

  const getSelectedColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 border-purple-500 ring-2 ring-purple-500',
      blue: 'bg-blue-100 border-blue-500 ring-2 ring-blue-500',
      green: 'bg-green-100 border-green-500 ring-2 ring-green-500',
      orange: 'bg-orange-100 border-orange-500 ring-2 ring-orange-500',
      red: 'bg-red-100 border-red-500 ring-2 ring-red-500',
      indigo: 'bg-indigo-100 border-indigo-500 ring-2 ring-indigo-500',
      pink: 'bg-pink-100 border-pink-500 ring-2 ring-pink-500',
      gray: 'bg-gray-100 border-gray-500 ring-2 ring-gray-500',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {editPost ? 'Edit Update' : 'Post Update'}
              </h2>
              <p className="text-sm text-gray-500">
                {step === 1 ? 'Select the type of update' : 'Write your update'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Step 1: Type Selection */}
          {step === 1 && (
            <div className="p-6">
              <div className="grid grid-cols-2 gap-3">
                {POST_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedType === type.value;
                  return (
                    <button
                      key={type.value}
                      onClick={() => handleTypeSelect(type.value)}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? getSelectedColorClasses(type.color)
                          : getColorClasses(type.color)
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs opacity-75">{type.labelCn}</div>
                        <div className="text-xs mt-1 opacity-60">{type.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Content Form */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Type Indicator */}
              {selectedTypeInfo && (
                <div className="flex items-center gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => !editPost && setStep(1)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      getColorClasses(selectedTypeInfo.color)
                    } ${!editPost ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <selectedTypeInfo.icon className="w-4 h-4" />
                    {selectedTypeInfo.label}
                    {!editPost && <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., XYZ-001 Enters Phase 2 Clinical Trials"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Content <span className="text-red-500">*</span>
                  </label>

                  {/* AI Assistant Button */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAiMenu(!showAiMenu)}
                      disabled={aiLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {aiLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          AI Writing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          AI Assist
                        </>
                      )}
                    </button>

                    {showAiMenu && (
                      <>
                        <div className="fixed inset-0" onClick={() => setShowAiMenu(false)} />
                        <div className="absolute right-0 top-10 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <button
                            type="button"
                            onClick={() => handleAiAssist('generate')}
                            disabled={!title.trim()}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Wand2 className="w-4 h-4 text-purple-500" />
                            <div className="text-left">
                              <div className="font-medium">Generate</div>
                              <div className="text-xs text-gray-500">
                                {title.trim() ? 'Write content from scratch' : 'Enter a title first'}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAiAssist('improve')}
                            disabled={!content}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <Sparkles className="w-4 h-4 text-blue-500" />
                            <div className="text-left">
                              <div className="font-medium">Improve</div>
                              <div className="text-xs text-gray-500">Make content more compelling</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAiAssist('expand')}
                            disabled={!content}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <Maximize2 className="w-4 h-4 text-green-500" />
                            <div className="text-left">
                              <div className="font-medium">Expand</div>
                              <div className="text-xs text-gray-500">Add more detail</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAiAssist('summarize')}
                            disabled={!content}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <Minimize2 className="w-4 h-4 text-orange-500" />
                            <div className="text-left">
                              <div className="font-medium">Summarize</div>
                              <div className="text-xs text-gray-500">Make it more concise</div>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Share details about your update..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Include specific details like therapeutic area, development stage, or key metrics to help buyers find you.
                </p>
              </div>

              {/* External URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <LinkIcon className="w-4 h-4 inline mr-1" />
                  External Link (optional)
                </label>
                <input
                  type="text"
                  value={externalUrl}
                  onChange={handleExternalUrlChange}
                  placeholder="www.pfizer.com/news/..."
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    urlError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {urlError ? (
                  <p className="text-xs text-red-600 mt-1">{urlError}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Link to company websites, news sources, or regulatory sites. Personal blogs and social media are not allowed.
                  </p>
                )}
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibility
                </label>
                <div className="flex gap-3">
                  {VISIBILITY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        visibility === option.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="visibility"
                        value={option.value}
                        checked={visibility === option.value}
                        onChange={(e) => setVisibility(e.target.value)}
                        className="sr-only"
                      />
                      <div>
                        <div className="font-medium text-sm text-gray-900">{option.label}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !title || !content || !!urlError}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      {visibility === 'draft' ? 'Save Draft' : 'Publish'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
